// ANA-R3-GEOM-02 — the shared PolygonEditor primitive (thin Konva render layer
// over `usePolygonEditor`). Host-agnostic: it renders into a Konva Stage sized
// to the host's render surface and projects normalised geometry through the
// host-supplied `CoordinateTransform`. The Viewer (GEOM-03/04) and Tiles editor
// (GEOM-05) wire it in; this story builds the primitive only.
//
// VISUAL STATES (GEOM-02 a11y): the active edge + selected vertex have distinct
// states; the active vertex carries a visible focus ring. Colours come from the
// `mui-theme-tokens` palette (Signal Blue accent + intent), resolved by the
// caller's theme — the component reads them off the MUI theme so light/dark and
// the compact presets stay consistent.

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Circle, Group, Layer, Line, Stage } from "react-konva";
import type {
  CoordinateTransform,
  ScreenPoint,
} from "./CoordinateTransform.js";
import type { EditorMode, PolygonValue } from "./geometry.js";
import {
  HANDLE_HIT_RADIUS_PX,
  usePolygonEditor,
  type PolygonEditorApi,
} from "./usePolygonEditor.js";

/** Constant on-screen handle radius (px) regardless of zoom (GEOM-02 AC3). */
const HANDLE_RADIUS_PX = 5;
const ACTIVE_HANDLE_RADIUS_PX = 6;
const FOCUS_RING_RADIUS_PX = HANDLE_HIT_RADIUS_PX;

export interface PolygonEditorProps {
  /** Controlled value: `{ mode, points }`, points normalised 0..1 (no closing
   *  duplicate — closure is implied by mode). */
  value: PolygonValue;
  /** Fires on every geometry change (draft moves included). */
  onChange?: (value: PolygonValue) => void;
  /** Fires on close / draw-done / explicit host Save. */
  onCommit?: (value: PolygonValue) => void;
  /** Host↔screen transform (OSD viewport or canvas). */
  transform: CoordinateTransform;
  /** Render surface size in CSS px (the Stage size). */
  width: number;
  height: number;
  /** Disable all editing interactions (render-only). Default true = editable. */
  editable?: boolean;
  /** Accessible label for the editor region. */
  ariaLabel?: string;
  /** ANA-R4-VALID-01 — a finish was refused because the shape self-intersects. */
  onInvalidGeometry?: (reason: "self-intersecting") => void;
  /**
   * Imperative handle so a host can drive the undo API + commit from its own
   * toolbar / undo stack (GEOM-02 AC5, story 064 integration). Receives the
   * live {@link PolygonEditorApi}.
   */
  apiRef?: (api: PolygonEditorApi | null) => void;
}

export function PolygonEditor({
  value,
  onChange,
  onCommit,
  transform,
  width,
  height,
  editable = true,
  ariaLabel = "Polygon editor",
  onInvalidGeometry,
  apiRef,
}: PolygonEditorProps) {
  const theme = useTheme();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const regionId = useId();

  const api = usePolygonEditor({
    value,
    transform,
    ...(onChange ? { onChange } : {}),
    ...(onCommit ? { onCommit } : {}),
    ...(onInvalidGeometry ? { onInvalidGeometry } : {}),
    editable,
  });

  // Expose the imperative API to the host.
  useEffect(() => {
    apiRef?.(api);
    return () => apiRef?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- identity churns each render; host reads latest
  }, [api]);

  const { render } = api;

  // --- pointer → editor wiring (screen space relative to the wrapper) ---
  const toScreenPoint = useCallback(
    (e: { clientX: number; clientY: number }): ScreenPoint | null => {
      const node = wrapperRef.current;
      if (!node) return null;
      const rect = node.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    },
    [],
  );

  // Track a vertex drag at the wrapper level so a drag that leaves the Stage
  // still updates (mirrors the Viewer's window-level mouseup handling).
  const draggingRef = useRef(false);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!editable) return;
      if (e.button !== 0) return;
      const sp = toScreenPoint(e);
      if (!sp) return;
      const grabbed = api.beginVertexDrag(sp);
      if (grabbed !== null) {
        draggingRef.current = true;
        e.preventDefault();
      }
    },
    [editable, toScreenPoint, api],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const sp = toScreenPoint(e);
      if (!sp) return;
      if (draggingRef.current) {
        api.dragVertexTo(sp);
      } else {
        api.handlePointerMove(sp);
      }
    },
    [toScreenPoint, api],
  );

  const handleMouseUp = useCallback(() => {
    if (draggingRef.current) {
      draggingRef.current = false;
      api.endVertexDrag();
    }
  }, [api]);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!editable) return;
      // A click that concluded a drag is not also a surface click.
      if (draggingRef.current) return;
      const sp = toScreenPoint(e);
      if (!sp) return;
      api.handleSurfaceClick(sp);
    },
    [editable, toScreenPoint, api],
  );

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!editable) return;
      const sp = toScreenPoint(e);
      if (!sp) return;
      api.handleSurfaceDoubleClick(sp);
    },
    [editable, toScreenPoint, api],
  );

  // --- keyboard (a11y traversal + nudge + delete + undo/close/cancel) ---
  const [arrowNudgePx] = useState(1);
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!editable) return;
      switch (e.key) {
        case "Enter":
          if (api.closeOrFinishDraw()) e.preventDefault();
          break;
        case "Escape":
          api.cancel();
          e.preventDefault();
          break;
        case "Delete":
        case "Backspace":
          if (api.deleteActiveVertex()) e.preventDefault();
          break;
        case "Tab":
          // Shift+Tab → previous vertex, Tab → next (only while a vertex is
          // focused, so the editor doesn't trap the page's tab order at rest).
          if (render.activeVertex !== null) {
            api.focusNextVertex(e.shiftKey ? -1 : 1);
            e.preventDefault();
          }
          break;
        case "ArrowUp":
          api.nudgeActiveVertex(0, -nudge(e, arrowNudgePx));
          e.preventDefault();
          break;
        case "ArrowDown":
          api.nudgeActiveVertex(0, nudge(e, arrowNudgePx));
          e.preventDefault();
          break;
        case "ArrowLeft":
          api.nudgeActiveVertex(-nudge(e, arrowNudgePx), 0);
          e.preventDefault();
          break;
        case "ArrowRight":
          api.nudgeActiveVertex(nudge(e, arrowNudgePx), 0);
          e.preventDefault();
          break;
        case "z":
        case "Z":
          if (e.ctrlKey || e.metaKey) {
            if (api.undo()) e.preventDefault();
          }
          break;
        default:
          break;
      }
    },
    [editable, api, render.activeVertex, arrowNudgePx],
  );

  // --- colours from theme tokens ---
  const accent = theme.palette.primary.main;
  const stroke = accent;
  const fill = alpha(accent, 0.12);
  const activeEdgeColor = theme.palette.info.main;
  const selectedVertexFill = theme.palette.background.paper;
  const focusRing = accent;

  const flatLinePoints = (pts: ScreenPoint[]): number[] =>
    pts.flatMap((p) => [p.x, p.y]);

  // The line for the committed shape (closed modes append the first point).
  const committedLine = (() => {
    const pts = render.screenPoints;
    if (pts.length === 0) return null;
    const flat = flatLinePoints(pts);
    return flat;
  })();

  // Draft (in-progress draw) line + rubber-band.
  const draftLine = (() => {
    if (!render.draftScreenPoints) return null;
    const pts = [...render.draftScreenPoints];
    if (render.rubberBand) pts.push(render.rubberBand);
    return flatLinePoints(pts);
  })();

  return (
    <Box
      ref={wrapperRef}
      role="application"
      aria-label={ariaLabel}
      aria-describedby={`${regionId}-help`}
      tabIndex={editable ? 0 : -1}
      data-testid="polygon-editor"
      data-mode={render.mode}
      data-closed={render.closed ? "true" : "false"}
      data-drawing={render.drawing ? "true" : "false"}
      data-active-vertex={
        render.activeVertex === null ? "" : String(render.activeVertex)
      }
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onKeyDown={handleKeyDown}
      sx={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        cursor: editable ? "crosshair" : "default",
        outline: "none",
        "&:focus-visible": {
          outline: 2,
          outlineColor: "primary.main",
          outlineOffset: 2,
        },
      }}
    >
      <Stage width={width} height={height} listening={false}>
        <Layer listening={false}>
          {/* Committed shape body */}
          {committedLine ? (
            <Line
              points={committedLine}
              closed={render.closed}
              stroke={stroke}
              strokeWidth={2}
              {...(render.closed ? { fill } : {})}
              listening={false}
            />
          ) : null}

          {/* Draft (drawing) line + rubber-band preview */}
          {draftLine ? (
            <Line
              points={draftLine}
              closed={false}
              stroke={stroke}
              strokeWidth={2}
              dash={[4, 4]}
              listening={false}
            />
          ) : null}

          {/* Active-edge highlight (the edge after the selected vertex). */}
          {render.activeVertex !== null && render.screenPoints.length >= 2
            ? renderActiveEdge(
                render.screenPoints,
                render.activeVertex,
                render.closed,
                activeEdgeColor,
              )
            : null}

          {/* Vertex handles + focus ring on the active one. */}
          {render.screenPoints.map((p, i) => {
            const isActive = i === render.activeVertex;
            return (
              <Group key={i}>
                {isActive ? (
                  <Circle
                    x={p.x}
                    y={p.y}
                    radius={FOCUS_RING_RADIUS_PX}
                    stroke={focusRing}
                    strokeWidth={1.5}
                    dash={[2, 2]}
                    listening={false}
                  />
                ) : null}
                <Circle
                  x={p.x}
                  y={p.y}
                  radius={isActive ? ACTIVE_HANDLE_RADIUS_PX : HANDLE_RADIUS_PX}
                  fill={isActive ? selectedVertexFill : stroke}
                  stroke={stroke}
                  strokeWidth={isActive ? 2 : 1}
                  listening={false}
                />
              </Group>
            );
          })}

          {/* Draft vertices (during draw). */}
          {render.draftScreenPoints?.map((p, i) => (
            <Circle
              key={`draft-${String(i)}`}
              x={p.x}
              y={p.y}
              radius={HANDLE_RADIUS_PX}
              fill={stroke}
              listening={false}
            />
          ))}
        </Layer>
      </Stage>

      {/* SR-only usage help. */}
      <Box
        id={`${regionId}-help`}
        sx={{
          position: "absolute",
          width: 1,
          height: 1,
          overflow: "hidden",
          clip: "rect(0 0 0 0)",
          whiteSpace: "nowrap",
        }}
      >
        {editable
          ? "Click to add vertices. Tab to traverse vertices, arrow keys to nudge, Delete to remove, Enter to close, Escape to cancel, Control+Z to undo."
          : "Read-only geometry."}
      </Box>
    </Box>
  );
}

/** Highlight the edge that begins at the active vertex (distinct active-edge
 *  state). Returns the wrap-around edge for closed shapes too. */
function renderActiveEdge(
  pts: ScreenPoint[],
  activeIndex: number,
  closed: boolean,
  color: string,
) {
  const a = pts[activeIndex];
  const nextIndex =
    activeIndex + 1 < pts.length ? activeIndex + 1 : closed ? 0 : null;
  if (!a || nextIndex === null) return null;
  const b = pts[nextIndex];
  if (!b) return null;
  return (
    <Line
      points={[a.x, a.y, b.x, b.y]}
      stroke={color}
      strokeWidth={3}
      listening={false}
    />
  );
}

function nudge(e: React.KeyboardEvent, base: number): number {
  // Shift = coarse nudge (10×), matching keyboard-first-patterns.
  return e.shiftKey ? base * 10 : base;
}

/** `#RRGGBB` → `rgba(...,a)`; passes through non-hex colours unchanged. */
function alpha(color: string, a: number): string {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(color.trim());
  if (!m?.[1]) return color;
  const int = parseInt(m[1], 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  return `rgba(${String(r)}, ${String(g)}, ${String(b)}, ${String(a)})`;
}

export type { EditorMode };
